import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaClipboardList,
  FaCheckCircle,
  FaTimesCircle,
  FaSpinner,
  FaTrophy,
  FaChartBar,
  FaFilePdf,
} from "react-icons/fa";
import PageHeader from "../../../core/common/header/PageHeader";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

type ExamResult = {
  attemptId: number;
  studentId: number;
  studentName: string;
  examSetName: string;
  level: string;
  startedAtBDT: string;
  submittedAtBDT: string;
  totalScore: number;
  maxScore: number;
  percentage: number;
  passed: boolean;
  lesenScore: number;
  lesenMaxScore: number;
  horenScore: number;
  horenMaxScore: number;
  schreibenScore: number;
  schreibenMaxScore: number;
  sprechenScore: number;
  sprechenMaxScore: number;
  overallFeedback: string | null;
  feedbackBy: string | null;
  resultsPublished: boolean;
};

const StudentExamResult: React.FC = () => {
  const { attemptId } = useParams<{ attemptId: string }>();
  const navigate = useNavigate();

  const [result, setResult] = useState<ExamResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [pdfViewing, setPdfViewing] = useState(false);

  const fetchPdfResponse = async () => {
    const res = await fetch(
      `${API_BASE_URL}/api/student/exams/attempts/${attemptId}/result/pdf`,
      { credentials: "include" }
    );
    if (!res.ok) throw new Error("PDF fetch failed");
    const disposition = res.headers.get("Content-Disposition") ?? "";
    const match = disposition.match(/filename="?([^"]+)"?/);
    const filename = match ? match[1] : `exam-result-${attemptId}.pdf`;
    const blob = await res.blob();
    return { blob, filename };
  };

  const downloadPdf = async () => {
    if (!attemptId) return;
    setPdfDownloading(true);
    try {
      const { blob, filename } = await fetchPdfResponse();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("PDF download error:", err);
    } finally {
      setPdfDownloading(false);
    }
  };

  const viewPdf = async () => {
    if (!attemptId) return;
    setPdfViewing(true);
    try {
      const { blob } = await fetchPdfResponse();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (err) {
      console.error("PDF view error:", err);
    } finally {
      setPdfViewing(false);
    }
  };

  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (!hasFetchedRef.current) {
      console.log("🔵 Fetching result for attempt:", attemptId);
      hasFetchedRef.current = true;
      fetchResult();
    } else {
      console.log("⏭️ Skipping duplicate fetch");
    }
  }, []);

  const fetchResult = async () => {
    setLoading(true);
    try {
      console.log("📡 Making API call...");
      const response = await fetch(
        `${API_BASE_URL}/api/student/exams/attempts/${attemptId}/result`,
        { credentials: "include" }
      );

      if (response.ok) {
        const data = await response.json();
        const resultData = data.data || data;
        console.log("✅ Result loaded successfully");
        setResult(resultData);
      } else {
        console.error("❌ API returned error");
        setError("Prüfungsergebnis konnte nicht geladen werden");
      }
    } catch (err) {
      console.error("❌ Network error:", err);
      setError("Netzwerkfehler");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <FaSpinner className="animate-spin text-[#009fe3] text-4xl mx-auto mb-4" />
          <p className="text-gray-600">Ergebnisse werden geladen...</p>
        </div>
      </div>
    );
  }

  if (error || !result) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full text-center">
          <FaTimesCircle className="text-red-500 text-5xl mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Ergebnisse können nicht geladen werden
          </h2>
          <p className="text-gray-600 mb-6">
            {error || "Ergebnis nicht gefunden"}
          </p>
          <button
            onClick={() => navigate("/student/exams/my-exams")}
            className="px-6 py-3 bg-[#009fe3] text-white rounded-lg font-semibold hover:bg-cyan-600 transition-all"
          >
            Zurück zu Meine Prüfungen
          </button>
        </div>
      </div>
    );
  }

  const getModulePercentage = (score: number, max: number) => {
    return max > 0 ? ((score / max) * 100).toFixed(1) : "0.0";
  };

  const getFailureReason = () => {
    if (result.passed) return null;

    const level = result.level?.toUpperCase();
    const lesenPct = parseFloat(
      getModulePercentage(result.lesenScore, result.lesenMaxScore)
    );
    const horenPct = parseFloat(
      getModulePercentage(result.horenScore, result.horenMaxScore)
    );
    const schreibenPct = parseFloat(
      getModulePercentage(result.schreibenScore, result.schreibenMaxScore)
    );
    const sprechenPct = parseFloat(
      getModulePercentage(result.sprechenScore, result.sprechenMaxScore)
    );

    if (level === "A1" || level === "A2") {
      const writtenTotal =
        result.lesenScore + result.horenScore + result.schreibenScore;
      const writtenMax =
        result.lesenMaxScore + result.horenMaxScore + result.schreibenMaxScore;
      const writtenPct =
        writtenMax > 0 ? (writtenTotal * 100.0) / writtenMax : 0;

      if (writtenPct < 60 && sprechenPct < 60) {
        return `Nicht bestanden: Kombinierte schriftliche Punktzahl (${writtenPct.toFixed(
          1
        )}%) und Sprechen (${sprechenPct.toFixed(1)}%) liegen beide unter 60%`;
      } else if (writtenPct < 60) {
        return `Nicht bestanden: Kombinierte schriftliche Punktzahl (${writtenPct.toFixed(
          1
        )}%) liegt unter 60%`;
      } else if (sprechenPct < 60) {
        return `Nicht bestanden: Sprechen-Punktzahl (${sprechenPct.toFixed(
          1
        )}%) liegt unter 60%`;
      }
    } else {
      const failedModules = [];
      if (lesenPct < 60) failedModules.push(`Lesen (${lesenPct.toFixed(1)}%)`);
      if (horenPct < 60) failedModules.push(`Hören (${horenPct.toFixed(1)}%)`);
      if (schreibenPct < 60)
        failedModules.push(`Schreiben (${schreibenPct.toFixed(1)}%)`);
      if (sprechenPct < 60)
        failedModules.push(`Sprechen (${sprechenPct.toFixed(1)}%)`);

      if (failedModules.length > 0) {
        return `Nicht bestanden: ${failedModules.join(", ")} unter 60%`;
      }
    }

    return "Nicht bestanden: Bestehensanforderungen nicht erfüllt";
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <PageHeader
        icon={<FaTrophy />}
        title="Prüfungsergebnis"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/student/student-dashboard" },
          { label: "Meine Prüfungen", to: "/student/exams/my-exams" },
          { label: "Ergebnis" },
        ]}
      />

      {/* Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {/* Overall Result Card */}
        <div
          className={`rounded-xl shadow-2xl p-8 mb-8 ${
            result.passed
              ? "bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-400"
              : "bg-gradient-to-br from-red-50 to-rose-50 border-2 border-red-400"
          }`}
        >
          <div className="text-center">
            {result.passed ? (
              <FaCheckCircle className="text-green-600 text-6xl mx-auto mb-4" />
            ) : (
              <FaTimesCircle className="text-red-600 text-6xl mx-auto mb-4" />
            )}
            <h2
              className={`text-3xl font-bold mb-2 ${
                result.passed ? "text-green-900" : "text-red-900"
              }`}
            >
              {result.passed ? "Herzlichen Glückwunsch!" : "Nicht bestanden"}
            </h2>

            {!result.passed && (
              <div className="bg-red-100 border border-red-300 rounded-lg p-3 mb-4 mt-4">
                <p className="text-red-900 text-sm font-semibold">
                  {getFailureReason()}
                </p>
              </div>
            )}
            <p className="text-gray-700 mb-6">
              {result.passed
                ? "Sie haben die Prüfung erfolgreich bestanden!"
                : "Üben Sie weiter und versuchen Sie es erneut!"}
            </p>

            <div className="flex items-center justify-center gap-8 mb-6">
              <div>
                <div className="text-5xl font-bold text-gray-900">
                  {result.totalScore}
                </div>
                <div className="text-sm text-gray-600">
                  von {result.maxScore}
                </div>
              </div>
              <div>
                <div
                  className={`text-5xl font-bold ${
                    result.passed ? "text-green-600" : "text-red-600"
                  }`}
                >
                  {result.percentage.toFixed(1)}%
                </div>
                <div className="text-sm text-gray-600">Prozent</div>
              </div>
            </div>

            <div className="flex items-center justify-center gap-3">
              <span
                className={`px-4 py-2 rounded-full font-semibold ${
                  result.passed
                    ? "bg-green-600 text-white"
                    : "bg-red-600 text-white"
                }`}
              >
                {result.level}
              </span>
              <span className="px-4 py-2 rounded-full font-semibold bg-gray-200 text-gray-800">
                {result.passed ? "BESTANDEN" : "NICHT BESTANDEN"}
              </span>
            </div>
          </div>
        </div>

        {/* Module Breakdown */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <FaChartBar className="text-[#009fe3]" />
            Punkteübersicht nach Modulen
          </h3>

          <div className="space-y-6">
            {/* Lesen */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-gray-900">📖 Lesen</span>
                <span className="text-sm font-semibold text-gray-700">
                  {result.lesenScore}/{result.lesenMaxScore} (
                  {getModulePercentage(result.lesenScore, result.lesenMaxScore)}
                  %)
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                <div
                  className="bg-blue-600 h-4 rounded-full transition-all duration-500"
                  style={{
                    width: `${getModulePercentage(
                      result.lesenScore,
                      result.lesenMaxScore
                    )}%`,
                  }}
                />
              </div>
            </div>

            {/* Hören */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-gray-900">🎧 Hören</span>
                <span className="text-sm font-semibold text-gray-700">
                  {result.horenScore}/{result.horenMaxScore} (
                  {getModulePercentage(result.horenScore, result.horenMaxScore)}
                  %)
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                <div
                  className="bg-purple-600 h-4 rounded-full transition-all duration-500"
                  style={{
                    width: `${getModulePercentage(
                      result.horenScore,
                      result.horenMaxScore
                    )}%`,
                  }}
                />
              </div>
            </div>

            {/* Schreiben */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-gray-900">
                  ✍️ Schreiben
                </span>
                <span className="text-sm font-semibold text-gray-700">
                  {result.schreibenScore}/{result.schreibenMaxScore} (
                  {getModulePercentage(
                    result.schreibenScore,
                    result.schreibenMaxScore
                  )}
                  %)
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                <div
                  className="bg-green-600 h-4 rounded-full transition-all duration-500"
                  style={{
                    width: `${getModulePercentage(
                      result.schreibenScore,
                      result.schreibenMaxScore
                    )}%`,
                  }}
                />
              </div>
            </div>

            {/* Sprechen */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-gray-900">🗣️ Sprechen</span>
                <span className="text-sm font-semibold text-gray-700">
                  {result.sprechenScore}/{result.sprechenMaxScore} (
                  {getModulePercentage(
                    result.sprechenScore,
                    result.sprechenMaxScore
                  )}
                  %)
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                <div
                  className="bg-orange-600 h-4 rounded-full transition-all duration-500"
                  style={{
                    width: `${getModulePercentage(
                      result.sprechenScore,
                      result.sprechenMaxScore
                    )}%`,
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Overall Feedback */}
        {result.overallFeedback && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Mentor-Feedback
            </h3>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-gray-800 whitespace-pre-wrap">
                {result.overallFeedback}
              </p>
              {result.feedbackBy && (
                <p className="text-sm text-gray-600 mt-3">
                  — {result.feedbackBy}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Exam Details */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <h3 className="text-xl font-bold text-gray-900 mb-4">
            Prüfungsdetails
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Studentenname:</span>
              <span className="ml-2 font-semibold text-gray-900">
                {result.studentName}
              </span>
            </div>
            <div>
              <span className="text-gray-600">Prüfung:</span>
              <span className="ml-2 font-semibold text-gray-900">
                {result.examSetName}
              </span>
            </div>
            <div>
              <span className="text-gray-600">Niveau:</span>
              <span className="ml-2 font-semibold text-gray-900">
                {result.level}
              </span>
            </div>
            <div>
              <span className="text-gray-600">Begonnen:</span>
              <span className="ml-2 font-semibold text-gray-900">
                {result.startedAtBDT}
              </span>
            </div>
            <div>
              <span className="text-gray-600">Eingereicht:</span>
              <span className="ml-2 font-semibold text-gray-900">
                {result.submittedAtBDT}
              </span>
            </div>
            <div>
              <span className="text-gray-600">Bestehensgrenze:</span>
              <span className="ml-2 font-semibold text-gray-900">60%</span>
            </div>
            <div>
              <span className="text-gray-600">Gesamtpunktzahl:</span>
              <span className="ml-2 font-semibold text-gray-900">
                {result.totalScore}/{result.maxScore}
              </span>
            </div>
            <div>
              <span className="text-gray-600">Ergebnis:</span>
              <span
                className={`ml-2 font-bold ${
                  result.passed ? "text-green-600" : "text-red-600"
                }`}
              >
                {result.passed ? "✓ BESTANDEN" : "✗ NICHT BESTANDEN"}
              </span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-4">
          <button
            onClick={() => {
              try {
                navigate("/student/my-exams");
              } catch (error) {
                console.error("Navigation error:", error);
              }
            }}
            className="flex-1 px-6 py-3 bg-[#009fe3] text-white rounded-lg font-semibold hover:bg-cyan-600 transition-colors"
          >
            Zurück zu Meine Prüfungen
          </button>
          {result.resultsPublished && (
            <>
              <button
                onClick={viewPdf}
                disabled={pdfViewing}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-[#009fe3] text-white rounded-lg font-semibold hover:bg-cyan-600 transition-colors disabled:opacity-60"
              >
                <FaFilePdf />
                {pdfViewing ? "Lädt..." : "PDF Anzeigen"}
              </button>
              <button
                onClick={downloadPdf}
                disabled={pdfDownloading}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors disabled:opacity-60"
              >
                <FaFilePdf />
                {pdfDownloading ? "Lädt..." : "PDF Herunterladen"}
              </button>
            </>
          )}
          <button
            onClick={() => navigate("/student/student-dashboard")}
            className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
          >
            Dashboard
          </button>
        </div>
      </main>
    </div>
  );
};

export default StudentExamResult;
